/** Вкладка «ФБС» — пополнение складов ФБС в 1С.
 *
 *  Менеджер выбирает склад ФБС (конфиг Э1), задаёт горизонт/окно/давность, жмёт «Рассчитать» и
 *  видит по каждой номенклатуре: сколько продаём с этого склада, сколько там лежит, сколько едет
 *  заказами на перемещение, на сколько дней хватит и сколько довезти. Колонка «К перевозу»
 *  правится руками (`fbsEdit.ts`) — из неё на Э4 соберётся `Заказ на перемещение` в 1С.
 *
 *  Без молчаливых фоллбэков: расчёт сам собой не запускается (спрос читается из DWH и живой 1С —
 *  это минуты), ошибка запроса показывается баннером с текстом бэка, а пустая таблица рисуется
 *  ТОЛЬКО когда расчёт прошёл и пополнять действительно нечего. Складов не заведено — так и
 *  сказано, с кнопкой в настройку, а не молчаливый пустой выпадающий список.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Pencil,
  Plus,
  RefreshCw,
  Settings2,
  Trash2,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/supply";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import { fmtDate, fmtDateTime, int0, plural } from "./format";
import { useFbsCalcSession } from "./fbsCalcSession";
import type { FbsCreatedMark } from "./fbsCalcSession";
import { clearQty, linesToSend, NO_EDITS, setQty, totalQty, zeroAll } from "./fbsEdit";
import type { FbsEdits } from "./fbsEdit";
import {
  buildSummary,
  parseSubmitError,
  sendableRows,
  submitBlockedReason,
  toRequestLines,
} from "./fbsSubmit";
import type { FbsSendRow, FbsSubmitFailure, FbsSubmitSummary } from "./fbsSubmit";
import { STOCK_SOURCE_LABEL, STOCK_SOURCE_SHORT, STOCK_SOURCES, stockSourceHeader } from "./fbsStock";
import type { FbsCardCtx } from "./fbsCards";
import { FbsNoSalesBlock } from "./SupplyFbsNoSales";
import { FbsNoWbCardBlock } from "./SupplyFbsNoWbCard";
import { FbsTable } from "./SupplyFbsTable";
import {
  LegalSelect,
  MpWarehouseSelect,
  OnecWarehouseField,
  OrganizationSelect,
} from "./FbsRefPickers";
import { draftErrors, EMPTY_CHANNEL, EMPTY_DRAFT, toBody } from "./fbsWarehouseDraft";
import type { WarehouseDraft } from "./fbsWarehouseDraft";
import { Hint } from "./shared";

const KEYS = {
  params: ["supply", "fbs", "params"],
  warehouses: ["supply", "fbs", "warehouses"],
  report: ["supply", "fbs", "report"],
  orders: ["supply", "fbs", "transfer-orders"],
};

const MP_LABEL: Record<string, string> = { wb: "WB", ozon: "Ozon" };

/** Время расчёта — «рассчитано в 14:05». Готового форматтера нет: `fmtDateTime` печатает ещё и
 *  дату, а расчёт всегда сегодняшний и в пределах сессии — важны часы и минуты. */
function fmtTime(ms: number): string {
  return new Date(ms).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

// ─────────────────────────── точка входа ───────────────────────────
/** Настройки расчёта и список складов известны ДО первого расчёта: считать сперва дефолтами, а
 *  потом ещё раз своими — это две минуты и промежуточные числа, которых никто не просил. */
export function SupplyFbsPage(): React.ReactElement {
  const prefs = useQuery({
    queryKey: KEYS.params,
    queryFn: api.getFbsParams,
    staleTime: Infinity,
  });
  const warehouses = useQuery({
    queryKey: KEYS.warehouses,
    queryFn: api.getFbsWarehouses,
    staleTime: Infinity,
  });
  if (prefs.error || warehouses.error)
    return (
      <p className="text-sm text-destructive">
        {extractErrorMessage(
          prefs.error ?? warehouses.error,
          "Не удалось загрузить настройки пополнения ФБС",
        )}
      </p>
    );
  if (!prefs.data || !warehouses.data)
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <RefreshCw className="size-4 animate-spin" /> Загружаю настройки пополнения ФБС…
      </p>
    );
  return <FbsBody prefs={prefs.data} warehouses={warehouses.data} />;
}

function FbsBody({
  prefs,
  warehouses,
}: {
  prefs: api.FbsParams;
  warehouses: api.FbsWarehouse[];
}): React.ReactElement {
  const qc = useQueryClient();
  const active = useMemo(() => warehouses.filter((w) => w.active), [warehouses]);
  const [setupOpen, setSetupOpen] = useState(false);
  const [whId, setWhId] = useState<number | null>(() => active[0]?.id ?? null);
  // выбранный склад мог быть выключен или удалён в настройке — не держим мёртвый id
  useEffect(() => {
    const first = active[0]?.id ?? null;
    if (whId !== null && !active.some((w) => w.id === whId)) setWhId(first);
    else if (whId === null && first !== null) setWhId(first);
  }, [active, whId]);

  const [draft, setDraft] = useState<Omit<api.FbsParams, "saved">>({
    horizon_days: prefs.horizon_days,
    window_days: prefs.window_days,
    stale_days: prefs.stale_days,
    round_to_box: prefs.round_to_box,
  });

  const session = useFbsCalcSession();
  const { applied, calcNonce, justCreated } = session;
  const report = useQuery({
    queryKey: [...KEYS.report, applied, calcNonce],
    queryFn: () => api.getFbsReport(applied as api.FbsReportQuery),
    enabled: applied !== null,
    retry: false,
    staleTime: Infinity,
  });

  const [edits, setEdits] = useState<FbsEdits>(NO_EDITS);
  const [editError, setEditError] = useState<{ nom: string; text: string } | null>(null);
  // новый расчёт — новые рекомендации: правки под старые числа уже не про них
  useEffect(() => {
    setEdits(NO_EDITS);
    setEditError(null);
  }, [report.dataUpdatedAt]);

  const saveMut = useMutation({
    mutationFn: api.saveFbsParams,
    onSuccess: (p) => {
      qc.setQueryData(KEYS.params, p);
      toast.success("Настройки пополнения ФБС сохранены");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось сохранить настройки")),
  });
  const resetMut = useMutation({
    mutationFn: api.resetFbsParams,
    onSuccess: (p) => {
      qc.setQueryData(KEYS.params, p);
      setDraft({
        horizon_days: p.horizon_days,
        window_days: p.window_days,
        stale_days: p.stale_days,
        round_to_box: p.round_to_box,
      });
      toast.success("Настройки сброшены к умолчанию");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось сбросить настройки")),
  });

  const data = report.data;
  const rows = useMemo(() => data?.rows ?? [], [data]);
  // в заказ уходят строки таблицы И введённое в «Без продаж ФБС» — сводка считает по обоим
  const sendRows = useMemo(() => (data ? sendableRows(data) : []), [data]);
  const lines = useMemo(() => linesToSend(sendRows, edits), [sendRows, edits]);
  const total = useMemo(() => totalQty(sendRows, edits), [sendRows, edits]);

  const calc = (): void => {
    if (whId === null) return;
    session.calc({ warehouse_id: whId, ...draft });
  };

  // Документ создан (или мог быть создан) — отчёт под ним устарел: «в пути» и остатки уже другие.
  // Убираем расчёт целиком вместе с кнопкой «Создать заказ на перемещение», правки — в ноль.
  const onCreated = (mark: FbsCreatedMark): void => {
    setEdits(NO_EDITS);
    setEditError(null);
    session.created(mark);
  };

  return (
    <div className="space-y-4">
      {setupOpen && (
        <FbsWarehousesModal warehouses={warehouses} onClose={() => setSetupOpen(false)} />
      )}

      {/* ── панель управления ── */}
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3 rounded-md border bg-card px-3 py-2.5">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Склад ФБС</span>
          {active.length === 0 ? (
            <span className="text-sm text-destructive">складов не заведено</span>
          ) : (
            <select
              value={whId ?? ""}
              onChange={(e) => setWhId(Number(e.target.value))}
              className="h-9 min-w-56 rounded-md border bg-background px-2 text-sm"
            >
              {active.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <NumParam
          label="Горизонт, дн"
          hint="На сколько дней продаж пополняем склад ФБС."
          value={draft.horizon_days}
          onChange={(v) => setDraft((d) => ({ ...d, horizon_days: v }))}
        />
        <NumParam
          label="Окно, дн"
          hint="За сколько дней берём историю заказов FBS, чтобы посчитать скорость продаж."
          value={draft.window_days}
          onChange={(v) => setDraft((d) => ({ ...d, window_days: v }))}
        />
        <NumParam
          label="«В пути», дн"
          hint="Заказ на перемещение старше этого возраста считается застрявшим: в «в пути» не учитывается, а показывается отдельным списком."
          value={draft.stale_days}
          onChange={(v) => setDraft((d) => ({ ...d, stale_days: v }))}
        />
        <label className="flex h-9 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.round_to_box}
            onChange={(e) => setDraft((d) => ({ ...d, round_to_box: e.target.checked }))}
            className="size-4"
          />
          округлять до короба
        </label>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={calc} disabled={whId === null || report.isFetching}>
            {report.isFetching ? <RefreshCw className="size-4 animate-spin" /> : null}
            Рассчитать
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => saveMut.mutate(draft)}
            disabled={saveMut.isPending}
          >
            Сохранить
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => resetMut.mutate()}
            disabled={resetMut.isPending || !prefs.saved}
            title={prefs.saved ? "Удалить свои настройки" : "Своих настроек нет — показаны умолчания"}
          >
            Сбросить
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSetupOpen(true)}>
            <Settings2 className="size-4" /> Склады ФБС
          </Button>
        </div>
      </div>

      {active.length === 0 && (
        <div className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2.5 text-sm">
          Ни одного активного склада ФБС не заведено — считать нечего. Заведите склад в настройке:
          целевой склад 1С, склад-источник, организация и каналы площадок, чей спрос он закрывает.
          <div className="mt-2">
            <Button size="sm" variant="outline" onClick={() => setSetupOpen(true)}>
              <Settings2 className="size-4" /> Настроить склады ФБС
            </Button>
          </div>
        </div>
      )}

      {/* ── ошибка расчёта: текст бэка целиком, а не пустая таблица ── */}
      {report.isError && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2.5 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
          <div>
            <p className="font-medium text-destructive">Расчёт не выполнен</p>
            <p className="mt-0.5 text-foreground">
              {extractErrorMessage(report.error, "Ошибка запроса")}
            </p>
          </div>
        </div>
      )}

      {report.isFetching && (
        <div className="flex flex-col items-center justify-center gap-3 rounded-md border bg-card py-12 text-center">
          <RefreshCw className="size-6 animate-spin text-muted-foreground" />
          <p className="text-sm font-medium">Считаю пополнение: заказы FBS, остатки и «в пути»…</p>
        </div>
      )}

      {applied === null && !report.isFetching && !report.isError && active.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border bg-card px-3 py-2.5 text-sm text-muted-foreground">
          {justCreated === null ? (
            <p>
              Проверьте параметры и нажмите «Рассчитать»: заказы читаются из DWH, остатки и «в
              пути» — из живой 1С, это занимает время, поэтому сам собой расчёт не запускается.
            </p>
          ) : justCreated.number !== null ? (
            <>
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <p>
                Заказ на перемещение{" "}
                <span className="font-mono text-foreground">{justCreated.number}</span> создан.
                Пересчитайте, прежде чем создавать следующий заказ.
              </p>
            </>
          ) : (
            <>
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
              <p>
                Ответ 1С оборвался: документ МОГ быть создан. Проверьте «Заказы на перемещение» в
                1С и пересчитайте, прежде чем создавать следующий заказ.
              </p>
            </>
          )}
        </div>
      )}

      {report.data && !report.isFetching && !report.isError && (
        <FbsReportView
          report={report.data}
          rows={rows}
          sendRows={sendRows}
          edits={edits}
          editError={editError}
          onQty={(nom, raw, maxQty) => {
            const res = setQty(edits, nom, raw, maxQty);
            setEdits(res.edits);
            setEditError(res.error === null ? null : { nom, text: res.error });
          }}
          onClear={(nom) => {
            setEdits((e) => clearQty(e, nom));
            setEditError((err) => (err?.nom === nom ? null : err));
          }}
          onZeroAll={() => {
            setEdits(zeroAll(sendRows.map((r) => r.nom_key)));
            setEditError(null);
          }}
          onResetAll={() => {
            setEdits(NO_EDITS);
            setEditError(null);
          }}
          linesCount={lines.length}
          total={total}
          calculatedAt={report.dataUpdatedAt}
          onCreated={onCreated}
        />
      )}
    </div>
  );
}

// ─────────────────────────── отчёт ───────────────────────────
function FbsReportView({
  report,
  rows,
  sendRows,
  edits,
  editError,
  onQty,
  onClear,
  onZeroAll,
  onResetAll,
  linesCount,
  total,
  calculatedAt,
  onCreated,
}: {
  report: api.FbsReport;
  rows: api.FbsReportRow[];
  /** Всё, что может уехать в заказ: `rows` + «Без продаж ФБС» (`fbsSubmit.sendableRows`). */
  sendRows: FbsSendRow[];
  edits: FbsEdits;
  editError: { nom: string; text: string } | null;
  onQty: (nom: string, raw: string, maxQty?: number) => void;
  onClear: (nomKey: string) => void;
  /** Ноль во всех строках — дальше руками. */
  onZeroAll: () => void;
  /** Снять все правки — вернуть расчёт. */
  onResetAll: () => void;
  linesCount: number;
  total: number;
  /** Когда пришли ЭТИ числа (`useQuery.dataUpdatedAt`): расчёт живёт минутами, а не вечно. */
  calculatedAt: number;
  onCreated: (mark: FbsCreatedMark) => void;
}): React.ReactElement {
  const p = report.params;
  const card: FbsCardCtx = {
    whName: report.warehouse.target_wh_name,
    stockAsOf: report.stock_as_of,
    staleDays: p.stale_days,
  };
  // Сводка документа считается из тех же строк и правок, что показаны в таблице (`fbsSubmit`).
  const summary = useMemo(
    () => buildSummary(report, sendRows, edits),
    [report, sendRows, edits],
  );
  const blocked = submitBlockedReason(summary);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const stockHead = stockSourceHeader(report);
  // ошибка ввода показывается у того блока, чья строка: у таблицы или у «Без продаж ФБС»
  const noSalesKeys = useMemo(
    () => new Set(report.no_sales_rows.map((r) => r.nom_key)),
    [report.no_sales_rows],
  );
  const noSalesError = editError !== null && noSalesKeys.has(editError.nom) ? editError : null;
  const tableError = editError !== null && !noSalesKeys.has(editError.nom) ? editError : null;
  return (
    <div className="space-y-4">
      {/* сводка расчёта */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-md border bg-card px-3 py-2 text-sm">
        <span>
          <b>{report.warehouse.name}</b>{" "}
          <span className="text-muted-foreground">
            ← {report.warehouse.source_wh_name}
          </span>
        </span>
        <span className="text-muted-foreground">
          заказы {fmtDate(report.orders_from)} — {fmtDate(report.orders_to)} ({p.window_days} дн),
          горизонт {p.horizon_days} дн, застрявшие старше {p.stale_days} дн
          {p.round_to_box ? ", округление до короба" : ""}
        </span>
        <span className="text-muted-foreground">
          рассчитано в <b className="text-foreground">{fmtTime(calculatedAt)}</b>
        </span>
        <span
          className={cn("text-muted-foreground", stockHead.title !== null && "cursor-help")}
          title={stockHead.title ?? undefined}
          data-testid="fbs-stock-source"
        >
          {stockHead.text}
        </span>
        {report.stock_sync_notes.length > 0 && (
          <details className="text-xs text-muted-foreground" data-testid="fbs-sync-notes">
            <summary className="cursor-pointer">
              замечания синка wh-helper ({report.stock_sync_notes.length})
            </summary>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {report.stock_sync_notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          </details>
        )}
        <span className="text-muted-foreground">
          строк <b className="text-foreground">{rows.length}</b>
        </span>
        {report.reconciliation.map((r) => (
          <span
            key={r.legal_name}
            className="text-muted-foreground"
            title={
              "Справочно: задания по складам продавца (wb_fbs_orders_api) против заказов FBS " +
              "того же юрлица в all_mp_orders за то же окно. Источники разные, сходиться " +
              "в ноль не обязаны."
            }
          >
            сверка {r.legal_name}:{" "}
            <b className="text-foreground">
              {int0(r.by_warehouse)} / {int0(r.all_mp)}
            </b>{" "}
            {r.diff_pct === null ? "(—)" : `(${r.diff_pct > 0 ? "+" : ""}${r.diff_pct}%)`}
          </span>
        ))}
        <span className="text-muted-foreground">
          к перемещению{" "}
          <b className="text-foreground">
            {int0(total)} шт в {plural(linesCount, "строке", "строках", "строках")}
          </b>
        </span>
        <span className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            title="Поставить 0 во всех строках (и в «Без продаж ФБС») — дальше проставить руками"
            onClick={onZeroAll}
          >
            Обнулить к перемещению
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={Object.keys(edits).length === 0}
            title="Снять все ручные правки — вернуть количество из расчёта"
            onClick={onResetAll}
          >
            Вернуть расчёт
          </Button>
          <Button
            size="sm"
            disabled={blocked !== null}
            title={blocked ?? "Создать «Заказ на перемещение» в 1С по строкам «К перевозу»"}
            onClick={() => setConfirmOpen(true)}
          >
            Создать заказ на перемещение
          </Button>
        </span>
      </div>

      {confirmOpen && (
        <FbsSubmitDialog
          warehouseId={report.warehouse.id}
          summary={summary}
          onClose={() => setConfirmOpen(false)}
          onCreated={onCreated}
        />
      )}

      {report.warnings.length > 0 && (
        <div className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm">
          <p className="font-medium">Предупреждения расчёта</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {report.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* таблица строк */}
      {rows.length === 0 ? (
        <p className="rounded-md border bg-card px-3 py-2.5 text-sm text-muted-foreground">
          Расчёт прошёл: ни продаж FBS, ни остатка, ни «в пути» за окно — возить по расчёту нечего.
        </p>
      ) : (
        <FbsTable
          rows={rows}
          params={p}
          card={card}
          edits={edits}
          editError={tableError}
          onQty={onQty}
          onClear={onClear}
        />
      )}

      {/* товары без продаж ФБС: ручной ввод, уходит в тот же заказ */}
      <FbsNoSalesBlock
        rows={report.no_sales_rows}
        edits={edits}
        editError={noSalesError}
        onQty={onQty}
      />

      {/* в комнате, но нет SKU WB в карточке 1С: только показ, в расчёт не входят */}
      <FbsNoWbCardBlock rows={report.no_wb_card_rows} card={card} />

      {/* созданные порталом заказы на перемещение */}
      <FbsCreatedOrders warehouseId={report.warehouse.id} />

      {/* застрявшие заказы на перемещение */}
      {report.stale_transfer_orders.length > 0 && (
        <div className="rounded-md border bg-card px-3 py-2.5 text-sm">
          <p className="font-medium">
            Застрявшие заказы на перемещение
            <Hint>
              Открытые заказы на этот склад старше {p.stale_days} дн. В «в пути» они НЕ учтены —
              раз товар не доехал за столько дней, считать его приехавшим нельзя. Разберитесь с ними
              в 1С: либо их довезут, либо их надо закрыть.
            </Hint>
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            старше {p.stale_days} дн, в «в пути» не учтены
          </p>
          <table className="mt-2 text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr>
                <th className="px-2 py-1 text-left font-medium">Номер</th>
                <th className="px-2 py-1 text-left font-medium">Дата</th>
                <th className="px-2 py-1 text-right font-medium">Штук</th>
              </tr>
            </thead>
            <tbody>
              {report.stale_transfer_orders.map((o) => (
                <tr key={o.number} className="border-t">
                  <td className="px-2 py-1 font-mono text-xs">{o.number}</td>
                  <td className="px-2 py-1">{fmtDate(o.date)}</td>
                  <td className="px-2 py-1 text-right">{int0(o.qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* SKU без номенклатуры 1С */}
      {report.unmapped_skus.length > 0 && (
        <div className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2.5 text-sm">
          <p className="font-medium">
            SKU без карточки 1С — их спрос в расчёт НЕ попал ({report.unmapped_skus.length})
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Заполните доп.реквизит SKU у номенклатуры в 1С, иначе эти продажи не пополняются.
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5 font-mono text-xs">
            {report.unmapped_skus.map((s) => (
              <span key={`${s.mp_code}:${s.mp_sku}`} className="rounded bg-background px-1.5 py-0.5">
                {MP_LABEL[s.mp_code] ?? s.mp_code}: {s.mp_sku}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────── создание заказа на перемещение (Э4) ───────────────────
/** Подтверждение перед записью в 1С: что за документ получится и из чего он состоит.
 *
 *  Диалог не закрывается по ошибке НИКОГДА: 409 раскладывается в строки превышения прямо здесь
 *  (человеку надо уменьшить именно их), а 500 означает, что документ в 1С УЖЕ создан — и это
 *  сказано словами, с номером, вместе с запретом жать «Создать» второй раз.
 */
function FbsSubmitDialog({
  warehouseId,
  summary,
  onClose,
  onCreated,
}: {
  warehouseId: number;
  summary: FbsSubmitSummary;
  onClose: () => void;
  /** Документ создан (номер) или МОГ быть создан (`number: null`, 500) — расчёт под ним устарел. */
  onCreated: (mark: FbsCreatedMark) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [failure, setFailure] = useState<FbsSubmitFailure | null>(null);
  const [result, setResult] = useState<api.FbsTransferOrderResult | null>(null);
  const [linesOpen, setLinesOpen] = useState(false);

  const createMut = useMutation({
    mutationFn: () =>
      api.createFbsTransferOrder({
        warehouse_id: warehouseId,
        horizon_days: summary.horizonDays,
        lines: toRequestLines(summary),
      }),
    onSuccess: (res) => {
      setFailure(null);
      setResult(res);
      qc.invalidateQueries({ queryKey: KEYS.orders });
      toast.success(`Заказ на перемещение ${res.doc_number} создан и проведён в 1С`);
    },
    onError: (e) => setFailure(parseSubmitError(e, "Заказ на перемещение не создан")),
  });

  // документ уже в 1С — повторное создание запрещено, кнопку убираем совсем
  const locked = result !== null || failure?.documentExists === true;

  /** Закрытие — единственная дверь наружу, и через неё страница узнаёт про документ: пока диалог
   *  открыт, результат надо дочитать (номер, ссылка на 1С), а кнопка «Создать заказ» под ним
   *  накрыта модалкой. Закрылись — расчёт устарел и уходит вместе с кнопкой (`onCreated`). */
  const close = (): void => {
    // пока POST летит, закрывать нечем: ответ придёт уже некуда, и «создан ли документ» узнать
    // будет неоткуда — а 1С к тому моменту его, возможно, уже записала
    if (createMut.isPending) {
      toast.info("Запрос в 1С ещё идёт — дождитесь ответа, иначе неизвестно, создан ли документ");
      return;
    }
    if (result !== null) onCreated({ number: result.doc_number });
    else if (failure?.documentExists === true) onCreated({ number: null });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/60"
      onClick={close}
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-lg border bg-background p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-base font-semibold">Заказ на перемещение в 1С</h3>
          <button type="button" onClick={close} className="rounded p-1 hover:bg-accent">
            <X className="size-4" />
          </button>
        </div>

        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Куда</dt>
          <dd className="font-medium">{summary.warehouseName}</dd>
          <dt className="text-muted-foreground">Откуда</dt>
          <dd>{summary.sourceName}</dd>
          <dt className="text-muted-foreground">Горизонт</dt>
          <dd>{summary.horizonDays} дн (уйдёт в комментарий документа)</dd>
          <dt className="text-muted-foreground">Строк</dt>
          <dd className="font-medium">{summary.linesCount}</dd>
          <dt className="text-muted-foreground">Штук</dt>
          <dd className="font-medium">{int0(summary.qtyTotal)}</dd>
        </dl>

        <button
          type="button"
          onClick={() => setLinesOpen((v) => !v)}
          className="mt-3 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          {linesOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          {linesOpen ? "Скрыть строки" : `Показать строки (${summary.linesCount})`}
        </button>
        {linesOpen && (
          <table className="mt-2 w-full text-sm">
            <thead className="border-b text-xs text-muted-foreground">
              <tr>
                <th className="px-2 py-1 text-left font-medium">Артикул</th>
                <th className="px-2 py-1 text-left font-medium">Товар</th>
                <th className="px-2 py-1 text-right font-medium">Штук</th>
              </tr>
            </thead>
            <tbody>
              {summary.lines.map((l) => (
                <tr key={l.nom_key} className="border-b last:border-0">
                  <td className="px-2 py-1 font-mono text-xs">{l.article || "—"}</td>
                  <td className="px-2 py-1">{l.nom_name || "без имени"}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{int0(l.qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {result !== null && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-emerald-500/50 bg-emerald-500/10 px-3 py-2.5 text-sm">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <div>
              <p className="font-medium">
                Создан и проведён: <span className="font-mono">{result.doc_number}</span>
              </p>
              <p className="mt-0.5 text-muted-foreground">
                от {fmtDate(result.doc_date)}, строк {result.lines_count}, штук{" "}
                {int0(result.qty_total)}. Найти в 1С: «Заказы на перемещение» → номер{" "}
                <span className="font-mono">{result.doc_number}</span> (ссылка{" "}
                <span className="font-mono text-xs">{result.doc_ref}</span>).
              </p>
              <p className="mt-0.5 text-muted-foreground">
                После закрытия расчёт сбросится: «в пути» и остатки уже другие — следующий заказ
                только по новому расчёту.
              </p>
            </div>
          </div>
        )}

        {failure !== null && (
          <div className="mt-3 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2.5 text-sm">
            <p className="flex items-start gap-2 font-medium text-destructive">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              {failure.documentExists ? "Документ в 1С уже создан — не повторяйте" : "Заказ не создан"}
            </p>
            <p className="mt-1 text-foreground">{failure.message}</p>
            {failure.over.length > 0 && (
              <table className="mt-2 w-full text-sm">
                <thead className="border-b text-xs text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1 text-left font-medium">Артикул</th>
                    <th className="px-2 py-1 text-left font-medium">Товар</th>
                    <th className="px-2 py-1 text-right font-medium">Просим</th>
                    <th className="px-2 py-1 text-right font-medium">Свободно</th>
                  </tr>
                </thead>
                <tbody>
                  {failure.over.map((o) => (
                    <tr key={o.nom_key} className="border-b last:border-0">
                      <td className="px-2 py-1 font-mono text-xs">{o.article || "—"}</td>
                      <td className="px-2 py-1">{o.nom_name || "без имени"}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{int0(o.qty)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{int0(o.source_free)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          {!locked && (
            <Button size="sm" onClick={() => createMut.mutate()} disabled={createMut.isPending}>
              {createMut.isPending ? <RefreshCw className="size-4 animate-spin" /> : null}
              Создать и провести
            </Button>
          )}
          <Button size="sm" variant={locked ? "default" : "ghost"} onClick={close}>
            {locked ? "Закрыть" : "Отмена"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** «Созданные заказы» — история ПОРТАЛА по этому складу (заведённые в 1С руками сюда не
 *  попадают: они видны в расчёте, который читает живую 1С). */
function FbsCreatedOrders({ warehouseId }: { warehouseId: number }): React.ReactElement | null {
  const orders = useQuery({
    queryKey: [...KEYS.orders, warehouseId],
    queryFn: () => api.getFbsTransferOrders(warehouseId),
  });
  if (orders.isError)
    return (
      <p className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
        {extractErrorMessage(orders.error, "Не удалось загрузить созданные заказы")}
      </p>
    );
  if (!orders.data || orders.data.length === 0) return null;
  return (
    <div className="rounded-md border bg-card px-3 py-2.5 text-sm">
      <p className="font-medium">
        Созданные заказы на перемещение
        <Hint>
          Что этот портал отправил в 1С по данному складу (последние 50). Заведённые в 1С руками
          сюда не попадают — они видны в колонке «В пути» расчёта.
        </Hint>
      </p>
      <table className="mt-2 w-full text-sm">
        <thead className="text-xs text-muted-foreground">
          <tr>
            <th className="px-2 py-1 text-left font-medium">Номер</th>
            <th className="px-2 py-1 text-left font-medium">Дата</th>
            <th className="px-2 py-1 text-right font-medium">Строк</th>
            <th className="px-2 py-1 text-right font-medium">Штук</th>
            <th className="px-2 py-1 text-right font-medium">Горизонт</th>
            <th className="px-2 py-1 text-left font-medium">Кто</th>
            <th className="px-2 py-1 text-left font-medium">Когда</th>
          </tr>
        </thead>
        <tbody>
          {orders.data.map((o) => (
            <tr key={o.id} className="border-t">
              <td className="px-2 py-1 font-mono text-xs">{o.doc_number}</td>
              <td className="px-2 py-1">{fmtDate(o.doc_date)}</td>
              <td className="px-2 py-1 text-right tabular-nums">{o.lines_count}</td>
              <td className="px-2 py-1 text-right tabular-nums">{int0(o.qty_total)}</td>
              <td className="px-2 py-1 text-right tabular-nums">{o.horizon_days} дн</td>
              <td className="px-2 py-1">
                {o.user_name ?? <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-2 py-1 text-muted-foreground">{fmtDateTime(o.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Параметр-число: пустое поле не превращается в ноль молча — оно просто не применяется. */
function NumParam({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  onChange: (v: number) => void;
}): React.ReactElement {
  return (
    <label className="flex flex-col gap-1">
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        <Hint>{hint}</Hint>
      </span>
      <Input
        value={String(value)}
        onChange={(e) => {
          const text = e.target.value.replace(/\D/g, "");
          if (text === "") return;
          onChange(Math.min(365, Math.max(1, Number(text))));
        }}
        className="h-9 w-20 text-right"
      />
    </label>
  );
}

// ─────────────────────────── настройка складов ФБС ───────────────────────────
// Черновик карточки и его проверка — в `fbsWarehouseDraft.ts` (чистая модель, под тестом),
// живые справочники для полей — в `FbsRefPickers.tsx`.

function FbsWarehousesModal({
  warehouses,
  onClose,
}: {
  warehouses: api.FbsWarehouse[];
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [edited, setEdited] = useState<{ id: number | null; draft: WarehouseDraft } | null>(null);
  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: KEYS.warehouses });
  };
  const createMut = useMutation({
    mutationFn: api.createFbsWarehouse,
    onSuccess: () => {
      invalidate();
      setEdited(null);
      toast.success("Склад ФБС заведён");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось завести склад")),
  });
  const updateMut = useMutation({
    mutationFn: (v: { id: number; body: api.FbsWarehouseBody }) =>
      api.updateFbsWarehouse(v.id, v.body),
    onSuccess: () => {
      invalidate();
      setEdited(null);
      toast.success("Склад ФБС сохранён");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось сохранить склад")),
  });
  const deleteMut = useMutation({
    mutationFn: api.deleteFbsWarehouse,
    onSuccess: () => {
      invalidate();
      toast.success("Склад ФБС удалён");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось удалить склад")),
  });
  const busy = createMut.isPending || updateMut.isPending || deleteMut.isPending;

  const save = (): void => {
    if (edited === null) return;
    const body = toBody(edited.draft);
    if (edited.id === null) createMut.mutate(body);
    else updateMut.mutate({ id: edited.id, body });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/60" onClick={onClose}>
      <div
        className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-lg border bg-background p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-base font-semibold">Склады ФБС</h3>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-accent">
            <X className="size-4" />
          </button>
        </div>
        <p className="mb-3 text-sm text-muted-foreground">
          Склад ФБС — это комната в 1С, которую мы пополняем со склада-источника под спрос
          перечисленных каналов площадок. Склады и организация выбираются из живых справочников
          1С, юрлицо и склад продавца — из кабинета площадки; ссылки (GUID) подставляются сами и
          показаны серым.
        </p>

        <table className="w-full text-sm">
          <thead className="border-b text-xs text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Название</th>
              <th className="px-2 py-1.5 text-left font-medium">Получатель</th>
              <th className="px-2 py-1.5 text-left font-medium">Источник</th>
              <th className="px-2 py-1.5 text-left font-medium">Каналы</th>
              <th className="px-2 py-1.5 text-left font-medium">Остаток ФБС</th>
              <th className="px-2 py-1.5 text-left font-medium">Активен</th>
              <th className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {warehouses.length === 0 && (
              <tr>
                <td colSpan={7} className="px-2 py-3 text-muted-foreground">
                  Складов ФБС пока нет.
                </td>
              </tr>
            )}
            {warehouses.map((w) => (
              <tr key={w.id} className="border-b last:border-0">
                <td className="px-2 py-1.5">{w.name}</td>
                <td className="px-2 py-1.5">{w.target_wh_name}</td>
                <td className="px-2 py-1.5">{w.source_wh_name}</td>
                <td className="px-2 py-1.5 text-xs text-muted-foreground">
                  {w.channels
                    .map(
                      (c) =>
                        `${MP_LABEL[c.mp_code] ?? c.mp_code}/${c.legal_name}` +
                        (c.mp_warehouse_id == null
                          ? ""
                          : ` → ${c.mp_warehouse_name || c.mp_warehouse_id}`),
                    )
                    .join(", ")}
                </td>
                <td className="px-2 py-1.5" title={STOCK_SOURCE_LABEL[w.stock_source]}>
                  {STOCK_SOURCE_SHORT[w.stock_source]}
                </td>
                <td className="px-2 py-1.5">
                  {w.active ? "да" : <span className="text-muted-foreground">нет</span>}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <button
                    type="button"
                    title="Править"
                    disabled={busy}
                    onClick={() =>
                      setEdited({
                        id: w.id,
                        draft: {
                          name: w.name,
                          target_wh_ref: w.target_wh_ref,
                          target_wh_name: w.target_wh_name,
                          source_wh_ref: w.source_wh_ref,
                          source_wh_name: w.source_wh_name,
                          org_key: w.org_key,
                          channels: w.channels.map((c) => ({ ...c })),
                          stock_source: w.stock_source,
                          active: w.active,
                        },
                      })
                    }
                    className="rounded p-1 hover:bg-accent disabled:opacity-40"
                  >
                    <Pencil className="size-4" />
                  </button>
                  <button
                    type="button"
                    title="Удалить"
                    disabled={busy}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Удалить склад ФБС «${w.name}»? Если по нему уже есть документы в 1С, сервер откажет — тогда выключите склад вместо удаления.`,
                        )
                      )
                        deleteMut.mutate(w.id);
                    }}
                    className="rounded p-1 text-destructive hover:bg-accent disabled:opacity-40"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {edited === null ? (
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={() =>
              setEdited({
                id: null,
                draft: { ...EMPTY_DRAFT, channels: [{ ...EMPTY_CHANNEL }] },
              })
            }
          >
            <Plus className="size-4" /> Добавить склад
          </Button>
        ) : (
          <WarehouseForm
            draft={edited.draft}
            isNew={edited.id === null}
            busy={busy}
            onChange={(draft) => setEdited((s) => (s === null ? s : { ...s, draft }))}
            onSave={save}
            onCancel={() => setEdited(null)}
          />
        )}
      </div>
    </div>
  );
}

function WarehouseForm({
  draft,
  isNew,
  busy,
  onChange,
  onSave,
  onCancel,
}: {
  draft: WarehouseDraft;
  isNew: boolean;
  busy: boolean;
  onChange: (d: WarehouseDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}): React.ReactElement {
  const errs = draftErrors(draft);
  const set = (patch: Partial<WarehouseDraft>): void => onChange({ ...draft, ...patch });
  return (
    <div className="mt-3 space-y-3 rounded-md border p-3">
      <p className="text-sm font-medium">{isNew ? "Новый склад ФБС" : `Правка: ${draft.name}`}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Название" value={draft.name} onChange={(v) => set({ name: v })} />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.active}
            onChange={(e) => set({ active: e.target.checked })}
            className="size-4"
          />
          активен (выключенный не считается, но виден здесь)
        </label>
        <OnecWarehouseField
          label="Склад-получатель (комната ФБС в 1С)"
          refKey={draft.target_wh_ref}
          name={draft.target_wh_name}
          onPick={(w) => set({ target_wh_ref: w.ref_key, target_wh_name: w.name })}
        />
        <OnecWarehouseField
          label="Склад-источник (откуда возим)"
          refKey={draft.source_wh_ref}
          name={draft.source_wh_name}
          onPick={(w) => set({ source_wh_ref: w.ref_key, source_wh_name: w.name })}
        />
        <OrganizationSelect value={draft.org_key} onChange={(v) => set({ org_key: v })} />
        <label className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            Источник остатка ФБС
            <Hint>
              Откуда колонка «Остаток ФБС». wh-helper — пул комнаты, то же число, что синк
              wh-helper публикует в WB (свободный 1С всех юрлиц комнаты − собрано, но не проведено
              − несобранные заказы); так заведена Москва. 1С — свободный остаток организации
              склада; для региональных складов, которые wh-helper не собирает.
            </Hint>
          </span>
          <select
            value={draft.stock_source}
            aria-label="Источник остатка ФБС"
            onChange={(e) => set({ stock_source: e.target.value as WarehouseDraft["stock_source"] })}
            className="h-9 rounded-md border bg-background px-2 text-sm"
          >
            <option value="" disabled>
              — выберите —
            </option>
            {STOCK_SOURCES.map((src) => (
              <option key={src} value={src}>
                {STOCK_SOURCE_LABEL[src]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div>
        <p className="mb-1 flex items-center gap-1 text-xs text-muted-foreground">
          Каналы (площадка / юрлицо / склад продавца)
          <Hint>
            Спрос FBS этих каналов закрывает данный склад. Склад продавца не выбран — берём
            весь спрос юрлица (так заведена Москва); выбран — только спрос этого склада
            площадки.
          </Hint>
        </p>
        <div className="space-y-1.5">
          {draft.channels.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <select
                value={c.mp_code}
                onChange={(e) => {
                  const next = draft.channels.map((x, j) =>
                    j === i
                      ? {
                          ...x,
                          mp_code: e.target.value as api.FbsChannel["mp_code"],
                          // юрлица и склады у площадок свои — переносить их нельзя
                          legal_name: "",
                          mp_warehouse_id: null,
                          mp_warehouse_name: null,
                        }
                      : x,
                  );
                  set({ channels: next });
                }}
                className="h-9 rounded-md border bg-background px-2 text-sm"
              >
                <option value="wb">WB</option>
                <option value="ozon">Ozon</option>
              </select>
              <LegalSelect
                mp={c.mp_code}
                value={c.legal_name}
                onChange={(legal) => {
                  // сменилось юрлицо — прежний склад площадки к нему не относится
                  const next = draft.channels.map((x, j) =>
                    j === i
                      ? { ...x, legal_name: legal, mp_warehouse_id: null, mp_warehouse_name: null }
                      : x,
                  );
                  set({ channels: next });
                }}
              />
              <MpWarehouseSelect
                mp={c.mp_code}
                legal={c.legal_name}
                value={c.mp_warehouse_id ?? null}
                currentName={c.mp_warehouse_name}
                onChange={(w) => {
                  const next = draft.channels.map((x, j) =>
                    j === i
                      ? {
                          ...x,
                          mp_warehouse_id: w === null ? null : w.id,
                          mp_warehouse_name: w === null ? null : w.name,
                        }
                      : x,
                  );
                  set({ channels: next });
                }}
              />
              <button
                type="button"
                title="Убрать канал"
                onClick={() => set({ channels: draft.channels.filter((_, j) => j !== i) })}
                className="rounded p-1 text-destructive hover:bg-accent"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          ))}
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="mt-1.5"
          onClick={() => set({ channels: [...draft.channels, { ...EMPTY_CHANNEL }] })}
        >
          <Plus className="size-4" /> Добавить канал
        </Button>
      </div>

      {errs.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5 text-xs text-destructive">
          {errs.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <Button size="sm" onClick={onSave} disabled={busy || errs.length > 0}>
          Сохранить
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
          Отмена
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): React.ReactElement {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-9" />
    </label>
  );
}
